<script lang="ts">
  import { onDestroy } from 'svelte';
  import { navigate } from 'svelte-routing';
  import {
    buildCountryQuizShareContent,
    copyToClipboard,
    formatHms,
  } from '@atlasguess/games';

  interface Props {
    /** Total time the run took, in milliseconds. */
    elapsedMs: number;
    /** First-attempt-correct count — drives the headline accuracy stat. */
    firstTryCorrect: number;
    /** Total countries in the run pool — denominator of accuracy. */
    total: number;
    /** Display name of the quiz (e.g. "Europe Countries Quiz"). */
    quizName: string;
    /**
     * Absolute URL of THIS quiz mode — used as the share link's href so
     * a recipient who clicks lands on the same mode. Computed by the
     * mode component from `window.location.origin` + the route path.
     */
    modeUrl: string;
    /** Resets the quiz in place — same pool re-shuffled, fresh timer. */
    onRetry: () => void;
    /** Optional URL to send users to a related practice mode. */
    practiceHref?: string;
  }

  let {
    elapsedMs,
    firstTryCorrect,
    total,
    quizName,
    modeUrl,
    onRetry,
    practiceHref,
  }: Props = $props();

  const accuracyPct = $derived(
    total === 0 ? 0 : Math.round((firstTryCorrect / total) * 100),
  );

  const shareContent = $derived(
    buildCountryQuizShareContent(quizName, elapsedMs, firstTryCorrect, total, modeUrl),
  );

  let copied = $state(false);
  let copyResetTimer: ReturnType<typeof setTimeout> | undefined;

  async function handleShare(): Promise<void> {
    const ok = await copyToClipboard(shareContent);
    if (!ok) return;
    copied = true;
    if (copyResetTimer) clearTimeout(copyResetTimer);
    copyResetTimer = setTimeout(() => {
      copied = false;
    }, 2000);
  }

  function goToPractice(): void {
    if (practiceHref) navigate(practiceHref);
  }

  onDestroy(() => {
    if (copyResetTimer) clearTimeout(copyResetTimer);
  });
</script>

<div class="quiz-end-screen">
  <div class="quiz-end-screen__panel">
    <div class="quiz-end-screen__title">{quizName}</div>

    <div class="quiz-end-screen__stats">
      <div class="quiz-end-screen__stat">
        <div class="quiz-end-screen__stat-label">Time</div>
        <div class="quiz-end-screen__stat-value">{formatHms(elapsedMs)}</div>
      </div>
      <div class="quiz-end-screen__stat">
        <div class="quiz-end-screen__stat-label">First-try</div>
        <div class="quiz-end-screen__stat-value">
          {accuracyPct}<span class="quiz-end-screen__stat-unit">%</span>
        </div>
        <div class="quiz-end-screen__stat-detail">
          {firstTryCorrect}/{total}
        </div>
      </div>
    </div>

    <div class="quiz-end-screen__actions">
      <button
        class="quiz-end-screen__btn quiz-end-screen__btn--primary"
        type="button"
        onclick={onRetry}
      >
        Retry
      </button>
      <button
        class="quiz-end-screen__btn quiz-end-screen__btn--secondary"
        type="button"
        onclick={handleShare}
      >
        {copied ? 'Copied!' : 'Share'}
      </button>
      {#if practiceHref}
        <button
          class="quiz-end-screen__btn quiz-end-screen__btn--secondary"
          type="button"
          onclick={goToPractice}
        >
          Practice Mode
        </button>
      {/if}
    </div>
  </div>
</div>
